import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Sparkles,
  Gem,
  Crown,
  Settings,
  User,
  LogOut,
  ChevronDown,
} from 'lucide-react';

const NavLink = ({ to, children, highlight = false }) => (
  <Link
    to={to}
    className={
      highlight
        ? 'inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rose-500'
        : 'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-zinc-300 transition-colors hover:bg-white/5 hover:text-white'
    }
  >
    {children}
  </Link>
);

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleProfile = () => setIsProfileOpen((v) => !v);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="fixed left-0 right-0 top-0 z-50 border-b border-white/10 bg-zinc-950/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
        <Link to={user?.hasPaid || user?.role === 'admin' ? '/app' : '/'} className="flex items-center gap-2.5">
          <img src="/img/logo.png" alt="Logo" className="h-8 w-8 object-contain" />
          <span className="text-lg font-semibold tracking-tight text-white">
            Borracha de Roupa
          </span>
        </Link>

        {user ? (
          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-1 md:flex">
              <NavLink to="/upload" highlight>
                <Sparkles className="h-4 w-4" /> Experimentar
              </NavLink>
              <NavLink to="/tokens">
                <Gem className="h-4 w-4" /> Tokens
              </NavLink>
              {!user.isVip && (
                <Link
                  to="/app?vip=true"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-amber-400 px-4 py-2 text-sm font-semibold text-zinc-900 transition-colors hover:bg-amber-300"
                >
                  <Crown className="h-4 w-4" /> VIP
                </Link>
              )}
              {user.role === 'admin' && (
                <NavLink to="/admin">
                  <Settings className="h-4 w-4" /> Admin
                </NavLink>
              )}
            </div>

            <div className="relative ml-1" ref={dropdownRef}>
              <button
                onClick={toggleProfile}
                className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 py-1.5 pl-1.5 pr-3 transition-colors hover:bg-white/10"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-rose-600 text-sm font-semibold text-white">
                  {user.nome?.charAt(0)?.toUpperCase() || 'U'}
                </span>
                <span className="hidden text-sm font-medium text-white sm:inline">
                  {user.nome?.split(' ')[0]}
                </span>
                <ChevronDown className="h-4 w-4 text-zinc-400" />
              </button>

              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-64 overflow-hidden rounded-xl border border-white/10 bg-zinc-900 shadow-xl">
                  <div className="border-b border-white/10 p-4">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-600 text-base font-semibold text-white">
                        {user.nome?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-white">{user.nome}</p>
                        <p className="truncate text-xs text-zinc-400">{user.email}</p>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between rounded-lg bg-white/5 px-3 py-2">
                      <span className="flex items-center gap-1.5 text-xs text-zinc-400">
                        <Gem className="h-3.5 w-3.5 text-rose-400" /> Tokens
                      </span>
                      <span className="text-sm font-semibold text-white">{user.tokens}</span>
                    </div>
                  </div>

                  <div className="p-1.5">
                    {[
                      { to: '/profile', label: 'Meu perfil', Icon: User },
                      { to: '/upload', label: 'Experimentar roupas', Icon: Sparkles },
                      { to: '/tokens', label: 'Carregar tokens', Icon: Gem },
                      ...(user.role === 'admin'
                        ? [{ to: '/admin', label: 'Painel admin', Icon: Settings }]
                        : []),
                    ].map(({ to, label, Icon }) => (
                      <Link
                        key={to}
                        to={to}
                        onClick={() => setIsProfileOpen(false)}
                        className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-zinc-300 transition-colors hover:bg-white/5 hover:text-white"
                      >
                        <Icon className="h-4 w-4" /> {label}
                      </Link>
                    ))}
                    <div className="my-1.5 border-t border-white/10" />
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-red-400 transition-colors hover:bg-red-500/10 hover:text-red-300"
                    >
                      <LogOut className="h-4 w-4" /> Sair da conta
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-1 md:flex">
              <NavLink to="/upload" highlight>
                <Sparkles className="h-4 w-4" /> Experimentar
              </NavLink>
              <NavLink to="/tokens">
                <Gem className="h-4 w-4" /> Tokens
              </NavLink>
              <Link
                to="/?vip=true"
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-400 px-4 py-2 text-sm font-semibold text-zinc-900 transition-colors hover:bg-amber-300"
              >
                <Crown className="h-4 w-4" /> VIP
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <NavLink to="/login">Entrar</NavLink>
              <Link
                to="/register"
                className="inline-flex items-center rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rose-500"
              >
                Cadastrar
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
